import { useState, useContext, useMemo } from "react";
import api from "../../../../service/api";
import NotyfContext from "../../../../contexts/NotyfContext";

export default function useSignatureSubmit({ id, studentData, fetchStudentData }) {
    const notyf = useContext(NotyfContext);

    const [form, setForm] = useState({
        training_date: null,
        training_session: null,
    });

    const [loadSignatures, setLoadSignatures] = useState(false);

    const updateForm = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };

    const selectedDateData = useMemo(() => {
        return studentData?.student?.student_training_dates
            ?.find(d => d.id === form.training_date);
    }, [studentData, form.training_date]);

    const sessionOptions = useMemo(() => {
        return selectedDateData?.session?.map(s => ({
            value: s,
            label: s.toUpperCase(),
        })) ?? [];
    }, [selectedDateData]);

    const payloadHandlers = {
        draw: (signature) => {
            const drawing = new FormData();
            drawing.append("data", signature.data);
            return drawing;
        },
        upload: (signature) => {
            const fd = new FormData(); 
            signature.files.forEach((fileItem, index) => {
                fd.append(`files[${index}]`, fileItem.file);
            });
            return fd;
        },
        selection: (signature) => {
            const selectedSignature = new FormData();
            selectedSignature.append('signature_id', signature.id);
            return selectedSignature;
        }
    };

    const buildRequestData = (signature) => {
        const handler = payloadHandlers[signature.type];

        if (!handler) {
            notyf?.error(`No handler defined for type "${signature.type}"`);
            return null;
        }

        const result = handler(signature);

        if (result instanceof FormData) {
            result.append("id", id);
            result.append("date", new Date().toISOString());
            result.append("type", signature.type);
            result.append("training_date", form.training_date);
            if (Array.isArray(form.training_session)) {
                form.training_session.forEach(session => {
                    result.append("training_session[]", session.value ?? session); // support object/value
                });
            } else if (form.training_session) {
                result.append("training_session[]", form.training_session.value ?? form.training_session);
            }

        }

        return result;
    };

    const handleSubmit = async (signature) => {
        const data = buildRequestData(signature);
        if (!form.training_date || !form.training_session) {
            notyf?.error("Training date and session are required");
            return;
        }

        if (!data) return;

        setLoadSignatures(true);

        try {
            const config = data instanceof FormData
                ? { headers: { "Content-Type": "multipart/form-data" } }
                : {};

            await api.post("tms/batches/bca-attendance/store", data, config);
            notyf?.success("Signature saved successfully!");
        } catch (error) {
            notyf?.error(error.response?.data?.message || "Error saving signature");
        } finally {
            fetchStudentData?.({ id });
            setLoadSignatures(false);
        }
    };

    return {
        form,
        updateForm,
        sessionOptions,
        selectedDateData,
        handleSubmit,
        loadSignatures,
    };
}
